import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Menu, Spin, Select, Button } from "antd";
import {
  CalendarOutlined,
  FolderOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import InfiniteScroll from "react-infinite-scroll-component";
import { svg } from "../../../Assets/svg";
import { formatToLocalDate, useDebounce } from "../../../helpers/utils";
import {
  getDecryptedName,
  isAdminSubAdminLoggedIn,
} from "../../../helpers/bycrypt";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import { RootState, useAppSelector } from "../../../store";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NoDataMessage from "../../Components/NoData";
import NotificationButton from "../../Components/NotificationButton";
import {
  fetchProjectsQueryFn,
  deleteProjectAPI,
  archiveProjectAPI,
} from "./projectApis";
import RenameProjectModal from "./Components/RenameProjectModal";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";

interface ProjectItem {
  _id: string;
  project_no: string;
  title: string;
  folder_name?: string;
  name?: string; // Client name
  project_status?: string;
  created_at: string;
  updated_at?: string;
  customer_id?: {
    company_name?: string;
  };
}

const LIMIT = 40;
export const PROJECT_SCROLL_KEY = "projectsScrollPosition";
export const PROJECT_SEARCH_KEY = "projectsSearchTerm";
export const PROJECT_REFETCH_KEY = "projectsRefetchRequired";
export const PROJECT_ARCHIVE_FILTER_KEY = "projectsArchiveFilter";

const getProjectStatusColor = (status: string | undefined | null) => {
  if (!status) return "inherit";
  const s = status.trim();

  // Design Requests
  if (s === "ToDo" || s === "To Do") return "#949494";
  if (s === "InProgress" || s === "In Progress") return "#167fff";
  if (s === "OnHold" || s === "On Hold") return "#d0bc00";
  if (s === "Completed" || s === "Approved") return "#419f20";
  if (s === "Quote Created") return "#436b81";
  if (s === "Issue") return "#ff8000";
  if (s === "Blocked") return "#ff0004";

  // Quotes & Orders
  if (s === "draft") return "#949494";
  if (s === "saved") return "#d0bc00";
  if (s === "sent") return "#167fff";
  if (s === "design_pending") return "#ff8000";
  if (s === "design_approved") return "#8000ff";
  if (s === "approved" || s === "approved_override") return "#419f20";
  if (s === "expired") return "#ff0004";
  if (s === "order_created") return "#436b81";
  if (s === "pending_admin_approval") return "#EE029C";
  if (s === "admin_approved") return "#008886";

  if (s === "production_planning") return "#8000ff";
  if (s === "production_started") return "#419f20";
  if (s === "ready_for_dispatch") return "#167fff";
  if (s === "dispatched") return "#d0bc00";
  if (s === "delivered") return "#ff54dd";
  if (s === "under_installation") return "#620e21";
  if (s === "installed") return "#ff8000";
  if (s === "closed") return "#000000";

  return "#949494";
};

const formatStatusLabel = (status: string) => {
  if (!status) return "";
  if (status === "ToDo") return "To Do";
  if (status === "InProgress") return "In Progress";
  if (status === "OnHold") return "On Hold";

  if (status.includes("_")) {
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { authLoading } = useAppSelector((state: RootState) => state.loading);
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.PROJECTS,
  );

  const scrollableDivRef = useRef<HTMLDivElement | null>(null);
  const scrollRestoredRef = useRef(false);
  const queryClient = useQueryClient();
  const scrollableDivId = useMemo(
    () => `projectsScrollDiv-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const [projectToDelete, setProjectToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const [archiveState, setArchiveState] = useState({
    _id: null,
    makeArchive: false,
  });
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);

  // ─── On mount: if returning from an action that requires refetch ─────────
  useEffect(() => {
    if (sessionStorage.getItem(PROJECT_REFETCH_KEY) === "1") {
      sessionStorage.removeItem(PROJECT_REFETCH_KEY);
      sessionStorage.removeItem(PROJECT_SCROLL_KEY);
      sessionStorage.removeItem(PROJECT_SEARCH_KEY);
      sessionStorage.removeItem(PROJECT_ARCHIVE_FILTER_KEY);
      queryClient.removeQueries({ queryKey: ["projects"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isMounting, setIsMounting] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setIsMounting(false), 20);
    return () => clearTimeout(timer);
  }, []);

  const [searchTerm, setSearchTerm] = useState<string>(() => {
    return sessionStorage.getItem(PROJECT_SEARCH_KEY) || "";
  });
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [archiveFilter, setArchiveFilter] = useState<boolean>(() => {
    return sessionStorage.getItem(PROJECT_ARCHIVE_FILTER_KEY) === "true";
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 991.98);

  const toggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const getFilterCount = () => {
    let count = 0;
    if (archiveFilter) count++;
    return count;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (searchTerm) {
      sessionStorage.setItem(PROJECT_SEARCH_KEY, searchTerm);
    } else {
      sessionStorage.removeItem(PROJECT_SEARCH_KEY);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (archiveFilter) {
      sessionStorage.setItem(PROJECT_ARCHIVE_FILTER_KEY, "true");
    } else {
      sessionStorage.removeItem(PROJECT_ARCHIVE_FILTER_KEY);
    }
  }, [archiveFilter]);

  const queryFilters = useMemo(
    () => ({
      search: debouncedSearch,
      is_archived: archiveFilter.toString(),
    }),
    [debouncedSearch, archiveFilter],
  );

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["projects", queryFilters],
    queryFn: ({ pageParam = 1, signal }) =>
      fetchProjectsQueryFn(
        {
          page: pageParam as number,
          limit: LIMIT,
          ...queryFilters,
        },
        signal,
      ),
    getNextPageParam: (lastPage: any, allPages) => {
      const fetched = allPages.reduce((acc: number, p: any) => {
        const items = Array.isArray(p)
          ? p
          : Array.isArray(p?.data)
          ? p.data
          : [];
        return acc + items.length;
      }, 0);
      const total = Array.isArray(lastPage)
        ? lastPage.length
        : typeof lastPage?.total === "number"
        ? lastPage.total
        : fetched;
      return fetched < total ? allPages.length + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    initialPageParam: 1,
    enabled: USER_PERMISSIONS.READ,
  });

  const isDebouncing = searchTerm !== debouncedSearch;
  const showLoading =
    isLoading ||
    isMounting ||
    isDebouncing ||
    (isFetching && !isFetchingNextPage);

  const projectsList: ProjectItem[] = useMemo(
    () =>
      queryData
        ? queryData.pages.flatMap((p: any) =>
            Array.isArray(p) ? p : Array.isArray(p?.data) ? p.data : [],
          )
        : [],
    [queryData],
  );

  const totalCount: number = useMemo(() => {
    if (!queryData?.pages?.[0]) return 0;
    const firstPage = queryData.pages[0];
    if (Array.isArray(firstPage)) return firstPage.length;
    return typeof firstPage?.total === "number"
      ? firstPage.total
      : projectsList.length;
  }, [queryData, projectsList.length]);

  useLayoutEffect(() => {
    if (
      !scrollRestoredRef.current &&
      projectsList.length > 0 &&
      !isLoading &&
      !isMounting
    ) {
      const savedScroll = sessionStorage.getItem(PROJECT_SCROLL_KEY);
      if (savedScroll) {
        const target = scrollableDivRef.current;
        if (target) {
          target.scrollTop = parseInt(savedScroll, 10);
          requestAnimationFrame(() => {
            setTimeout(() => {
              target.scrollTop = parseInt(savedScroll, 10);
            }, 100);
          });
        }
        sessionStorage.removeItem(PROJECT_SCROLL_KEY);
      }
      scrollRestoredRef.current = true;
    }
  }, [projectsList, isLoading, isMounting]);

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleCardClick = (displayTitle: string) => {
    if (scrollableDivRef.current) {
      sessionStorage.setItem(
        PROJECT_SCROLL_KEY,
        String(scrollableDivRef.current.scrollTop),
      );
    }
    navigate(`/projects/${encodeURIComponent(displayTitle)}`);
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {/* ✅ View/Read Guard */}
        {!archiveFilter && USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="1"
            onClick={() => {
              setSelectedProject(data);
              setRenameModalOpen(true);
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Rename
            </div>
          </Menu.Item>
        )}
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="3"
            onClick={() => {
              setArchiveState({
                _id: data?._id,
                makeArchive: !!data?.is_archived ? false : true,
              });
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              {!!data?.is_archived ? "Unarchive" : "Archive"}
            </div>
          </Menu.Item>
        )}
        {!archiveFilter &&
          isAdminSubAdminLoggedIn() &&
          USER_PERMISSIONS.REMOVE && (
            <Menu.Item
              key="2"
              onClick={() => {
                setProjectToDelete(data);
              }}
              danger
            >
              <div className="d-flex align-items-center gap-2 svg-20">
                Delete
              </div>
            </Menu.Item>
          )}
      </Menu>
    );
  };

  return USER_PERMISSIONS.READ ? (
    <>
      <div
        id={scrollableDivId}
        ref={scrollableDivRef}
        className={`page-content h-100 px-40 py-40 ${isMobile ? "pb-0" : ""}`}
      >
        {/* Header Bar matching Quotations.tsx */}
        <div className="mb-md-3 mb-2 d-flex flex-column gap-md-2 gap-0">
          <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
            <h2 className="text-black fw-bolder page-title mb-0 order-sm-1 order-1 d-flex align-items-center gap-1">
              {archiveFilter ? "Archived Projects" : "Projects"}{" "}
              <span>
                {showLoading ? "" : totalCount ? `(${totalCount})` : "(0)"}
              </span>
            </h2>

            <div className="pageSearch quotationsSearch mw-100 position-relative d-inline-block order-sm-2 order-3 ms-auto">
              <input
                type="text"
                value={searchTerm}
                className="form-control form-control-lg"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search"
              />
              {searchTerm?.length ? (
                <span
                  className="cursor-pointer"
                  onClick={() => setSearchTerm("")}
                >
                  {svg?.searchClear}
                </span>
              ) : (
                <span>{svg?.search}</span>
              )}
            </div>

            <div className="d-flex gap-md-3 gap-2 order-sm-3 order-2">
              <div
                onClick={toggleFilter}
                role="button"
                aria-expanded={isFilterOpen}
                className="filterButton position-relative ms-sm-0 ms-auto"
              >
                {svg?.filterIcon}
                {getFilterCount() > 0 && (
                  <span className="position-absolute badge customBadgeFont rounded-pill bg-danger">
                    {getFilterCount()}
                  </span>
                )}
              </div>
              <NotificationButton />
            </div>
          </div>

          {/* Filter Drawer */}
          <div
            className={`filter-transition-container overflow-hidden ${
              isFilterOpen ? "is-open" : ""
            }`}
          >
            <div className="d-flex flex-wrap align-items-end bg-white p-2 rounded-3 mt-2 position-relative">
              <label className="filterTitle fw-bold fs-5 position-absolute d-flex align-items-center">
                Filters
              </label>
              <div className="row gx-md-3 gx-2 gy-sm-3 gy-2 w-100">
                {/* Archive Filter */}
                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Archive
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      style={{ width: "180px" }}
                      value={archiveFilter}
                      onChange={setArchiveFilter}
                      className="form-select form-select-lg"
                    >
                      <Select.Option value={false}>Unarchived</Select.Option>
                      <Select.Option value={true}>Archived</Select.Option>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid Content Area */}
        <style>{`
        @media (min-width: 1200px) {
          .project-card-col {
            flex: 0 0 12.5% !important;
            max-width: 12.5% !important;
          }
        }
      `}</style>

        {showLoading ? (
          <div
            className="d-flex justify-content-center align-items-center py-5"
            style={{ minHeight: "300px" }}
          >
            <Spin size="large" tip="Loading projects..." />
          </div>
        ) : projectsList.length === 0 ? (
          <div className="bg-white rounded-3 p-4 shadow-sm">
            <NoDataMessage
              icon={svg?.noFoldersIcon}
              text={
                searchTerm
                  ? "No project matching your search query was found."
                  : "There are currently no projects available."
              }
            />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={projectsList.length}
            next={handleLoadMore}
            hasMore={!!hasNextPage}
            loader={
              isFetchingNextPage ? (
                <div className="text-center p-3 w-100">
                  <Spin tip="Loading more projects..." />
                </div>
              ) : null
            }
            scrollableTarget={scrollableDivId}
            style={{ overflow: "visible" }}
          >
            <div className="row gy-md-4 gy-3">
              {projectsList.map((project) => {
                const displayTitle =
                  project.project_no || project.title || "00000";
                const decrypted =
                  project.name && project.name !== "N/A"
                    ? getDecryptedName(project.name)
                    : null;
                const clientName =
                  decrypted && decrypted !== "-" ? decrypted : null;
                const companyName = project?.customer_id?.company_name ?? "-";
                const createdDate = project.created_at
                  ? formatToLocalDate(project.created_at)
                  : "N/A";

                return (
                  <div
                    key={project._id || project.project_no}
                    className="col-6 col-sm-6 col-md-4 col-lg-3 project-card-col"
                  >
                    <div
                      className="card border-0 shadow-sm h-100 rounded-3 position-relative cursor-pointer overflow-hidden"
                      onClick={() => handleCardClick(displayTitle)}
                      style={{
                        backgroundColor: "#ffffff",
                        cursor: "pointer",
                      }}
                    >
                      <div className="card-body p-3 d-flex flex-column align-items-center text-center position-relative project-folder-card">
                        {/* Folder Graphic */}
                        {(USER_PERMISSIONS.MODIFY ||
                          USER_PERMISSIONS.REMOVE) && (
                          <div
                            className="position-absolute top-0 end-0 m-2 folder-action-menu"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Dropdown
                              overlay={() => MoreOptionsMenu(project)}
                              trigger={["click"]}
                              placement="bottomRight"
                              className="cursor-pointer"
                              overlayClassName="moreOptionDropdown"
                            >
                              <Button
                                type="text"
                                icon={
                                  <MoreOutlined style={{ fontSize: "18px" }} />
                                }
                                size="large"
                                style={{ padding: "4px 8px" }}
                              />
                            </Dropdown>
                          </div>
                        )}
                        <div
                          className="mb-2 mt-1 d-flex justify-content-center align-items-center"
                          style={{ height: "54px" }}
                        >
                          <div
                            style={{
                              transform: "scale(0.65)",
                              transformOrigin: "center",
                            }}
                          >
                            {svg?.folderIcon || (
                              <div style={{ fontSize: 44, color: "#FFC645" }}>
                                <FolderOutlined />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Project Number / Title */}
                        <h6
                          className="fw-bold text-dark mb-1 text-truncate w-100"
                          style={{ fontSize: "15px" }}
                          title={displayTitle}
                        >
                          {displayTitle}
                        </h6>

                        {/* Client Name */}
                        {companyName ? (
                          <div
                            className="text-secondary fw-medium mb-3 w-100"
                            style={{
                              fontSize: "13px",
                              lineHeight: "20px",
                              height: "40px",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={companyName}
                          >
                            {companyName}
                          </div>
                        ) : (
                          <div
                            className="text-secondary fw-medium mb-3 text-truncate w-100"
                            style={{ fontSize: "13px" }}
                          >
                            -
                          </div>
                        )}

                        {/* Status */}
                        {project.project_status ? (
                          <div
                            className="mb-3 fw-bold"
                            style={{
                              color: getProjectStatusColor(
                                project.project_status,
                              ),
                              fontSize: "13px",
                            }}
                          >
                            {formatStatusLabel(project.project_status)}
                          </div>
                        ) : (
                          <div
                            className="mb-3 fw-bold text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            -
                          </div>
                        )}

                        {/* Footer Date */}
                        <div
                          className="mt-auto w-100 pt-2 border-top border-light d-flex align-items-center justify-content-center gap-1 text-muted"
                          style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                        >
                          <CalendarOutlined style={{ fontSize: 12 }} />
                          <span>{createdDate}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </InfiniteScroll>
        )}

        {renameModalOpen && (
          <RenameProjectModal
            open={renameModalOpen}
            onClose={() => {
              setRenameModalOpen(false);
              setSelectedProject(null);
            }}
            projectData={selectedProject}
          />
        )}

        {projectToDelete && (
          <CommonConfirmationModal
            open={projectToDelete != null}
            title="Remove Project Folder, Are You Sure?"
            subTitle="Removing this project folder will permanently delete it along with all sub-folders and associated items, including estimates, design requests, quotations, and orders. This action cannot be undone. Please confirm before proceeding."
            loading={deleting}
            onSuccess={() => {
              setDeleting(true);
              dispatch(
                deleteProjectAPI({
                  payload: {
                    project_no:
                      projectToDelete.project_no || projectToDelete.folder_name,
                  },
                  onSuccess: () => {
                    setDeleting(false);
                    setProjectToDelete(null);
                    queryClient.removeQueries({ queryKey: ["projects"] });
                  },
                  onError: () => {
                    setDeleting(false);
                  },
                }),
              );
            }}
            onClose={() => setProjectToDelete(null)}
            confirmButtonText="REMOVE"
          />
        )}
      </div>

      <CommonConfirmationModal
        open={archiveState?._id != null}
        title={
          archiveState?.makeArchive === true
            ? "Archive Project, Are You Sure?"
            : "Unarchive Project, Are You Sure?"
        }
        subTitle={
          archiveState?.makeArchive === true
            ? "Archiving this project will move it to the archived list. You can restore it later if needed."
            : "Unarchiving this project will restore it to the main projects list."
        }
        loading={isArchiveLoading}
        onSuccess={() => {
          setIsArchiveLoading(true);
          const payload = {
            id: archiveState?._id,
            is_archived: archiveState?.makeArchive === true ? true : false,
          };
          dispatch(
            archiveProjectAPI({
              payload,
              onSuccess: () => {
                console.log("OnSucess");
                setIsArchiveLoading(false);
                queryClient.removeQueries({ queryKey: ["projects"] });
                setArchiveState({ _id: null, makeArchive: false });
              },
              onError: () => {
                setIsArchiveLoading(false);
              },
            }),
          );
        }}
        onClose={() => setArchiveState({ _id: null, makeArchive: false })}
        confirmButtonText={
          archiveState?.makeArchive === true ? "ARCHIVE" : "UNARCHIVE"
        }
      />
    </>
  ) : authLoading ? (
    <div
      className="d-flex justify-content-center align-items-center py-5"
      style={{ minHeight: "300px" }}
    >
      <Spin size="large" tip="Loading projects..." />
    </div>
  ) : (
    <NoPermissionPage />
  );
};

export default Projects;
